import {
  WSButton,
  WSElement,
  WSElementProps,
  WSFlexBox,
  WSList,
  WSSectionToolbar,
  WSText
} from "@wingspanhq/fe-component-library";
import { InvoiceStatus } from "@wingspanhq/payments/dist/interfaces";
import React from "react";
import { Route, useHistory, useRouteMatch } from "react-router-dom";
import { TablePayables } from "../../../Invoices/components/TablePayables";
import { CompletedDeductionDetails } from "../../../Invoices/screens/deductions/CompletedDuductionDetails";
import { ScheduledDeductionDetails } from "../../../Invoices/screens/deductions/ScheduledDuductionDetails";
import { PayableDetails } from "../../../Invoices/screens/payables/PayableDetails";
import { WSQueries } from "../../../query/WSQuery";
import { useQueryPayables } from "../../../query/payments/queries";
import { useQueryAllPayeeRows } from "../../../query/search/payee/queries/useQueryAllPayeeRows";
import { DownloadCsv } from "./DownloadCsv";

const filtersOverdue = {
  status: {
    in: [InvoiceStatus.Overdue]
  }
};

const filtersOpen = {
  status: {
    in: [InvoiceStatus.Pending, InvoiceStatus.Open]
  }
};

const filtersPaid = {
  status: {
    in: [InvoiceStatus.Paid, InvoiceStatus.PaymentInTransit]
  }
};

type Props = WSElementProps;

export const PaymentHistory: React.FC<Props> = ({ ...elementProps }) => {
  const queryPayablesOverdue = useQueryPayables({
    filter: filtersOverdue,
    page: {
      size: 10
    }
  });
  const queryPayablesOpen = useQueryPayables({
    filter: filtersOpen,
    page: {
      size: 10
    }
  });
  const queryPayablesPaid = useQueryPayables({
    filter: filtersPaid,
    page: {
      size: 10
    }
  });
  const queryAllPayeeRows = useQueryAllPayeeRows();
  const match = useRouteMatch();
  const history = useHistory();

  return (
    <WSElement {...elementProps}>
      <Route
        path={`${match.path}/:payableId/deduction/scheduled/:deductionId`}
        component={ScheduledDeductionDetails}
      />
      <Route
        path={`${match.path}/:payableId/deduction/completed/:deductionId`}
        component={CompletedDeductionDetails}
      />
      <Route
        path={`${match.path}/:payableId`}
        component={PayableDetails}
        exact
      />

      <WSQueries
        queries={{
          queryPayablesOverdue,
          queryPayablesPaid,
          queryPayablesOpen,
          queryAllPayeeRows
        }}
      >
        {({
          queryPayablesOverdue: {
            data: { data: overduePayables }
          },
          queryPayablesPaid: {
            data: { data: paidPayables }
          },
          queryPayablesOpen: {
            data: { data: openPayables }
          },
          queryAllPayeeRows: { data: payeeRows }
        }) => (
          <WSList gap="2XL">
            {overduePayables.length > 0 && (
              <WSElement>
                <WSFlexBox
                  wrap="nowrap"
                  justify="space-between"
                  alignItems="center"
                >
                  <WSSectionToolbar title="Overdue invoices" />

                  <DownloadCsv
                    payeeRows={payeeRows}
                    filters={filtersOverdue}
                    fileName="Overdue payables"
                  />
                </WSFlexBox>

                <TablePayables
                  showHeaders
                  payeeRows={payeeRows}
                  payables={overduePayables}
                  onRowClick={({
                    data: {
                      payable: { payableId }
                    }
                  }) => {
                    history.push(`/payment/${payableId}`);
                  }}
                  showRefundedLabel
                />
                {queryPayablesOverdue.hasNextPage && (
                  <WSButton.Link
                    mt="M"
                    fullWidth
                    onClick={() => {
                      queryPayablesOverdue.fetchNextPage();
                    }}
                    loading={!!queryPayablesOverdue.isFetchingNextPage}
                  >
                    Load more
                  </WSButton.Link>
                )}
              </WSElement>
            )}

            {openPayables.length > 0 && (
              <WSElement>
                <WSFlexBox
                  wrap="nowrap"
                  justify="space-between"
                  alignItems="center"
                >
                  <WSSectionToolbar title="Open invoices" />
                  <DownloadCsv
                    payeeRows={payeeRows}
                    filters={filtersOpen}
                    fileName="Open payables"
                  />
                </WSFlexBox>

                <TablePayables
                  showHeaders
                  payables={openPayables}
                  payeeRows={payeeRows}
                  onRowClick={({
                    data: {
                      payable: { payableId }
                    }
                  }) => {
                    history.push(`/payment/${payableId}`);
                  }}
                  showRefundedLabel
                />

                {queryPayablesOpen.hasNextPage && (
                  <WSButton.Link
                    mt="M"
                    fullWidth
                    onClick={() => {
                      queryPayablesOpen.fetchNextPage();
                    }}
                    loading={!!queryPayablesOpen.isFetchingNextPage}
                  >
                    Load more
                  </WSButton.Link>
                )}
              </WSElement>
            )}
            {paidPayables.length > 0 && (
              <WSElement>
                <WSFlexBox
                  wrap="nowrap"
                  justify="space-between"
                  alignItems="center"
                >
                  <WSSectionToolbar title="Payment history" />
                  <DownloadCsv
                    payeeRows={payeeRows}
                    filters={filtersPaid}
                    fileName="Payment history"
                  />
                </WSFlexBox>

                <TablePayables
                  showHeaders
                  payables={paidPayables}
                  payeeRows={payeeRows}
                  onRowClick={({
                    data: {
                      payable: { payableId }
                    }
                  }) => {
                    history.push(`${match.path}/${payableId}`, {
                      backPath: match.path
                    });
                  }}
                  showRefundedLabel
                />

                {queryPayablesPaid.hasNextPage && (
                  <WSButton.Link
                    mt="M"
                    fullWidth
                    onClick={() => {
                      queryPayablesPaid.fetchNextPage();
                    }}
                    loading={!!queryPayablesPaid.isFetchingNextPage}
                  >
                    Load more
                  </WSButton.Link>
                )}
              </WSElement>
            )}

            {openPayables.length === 0 && paidPayables.length === 0 && (
              <WSElement>
                <WSSectionToolbar title="Payment history" />
                <WSText.ParagraphSm color="gray400">
                  No invoices found
                </WSText.ParagraphSm>
              </WSElement>
            )}
          </WSList>
        )}
      </WSQueries>
    </WSElement>
  );
};
